import React from 'react';
import { Brain, Shield, Zap, HandCoins, Handshake, Leaf, Scale, Radiation } from 'lucide-react';
import StatCard from '../../../StatCard';
import Decret from '../../../Decret';
import { mockSimGameState } from '../helpers';

const GameSimulationSlide = ({
    slide,
    slideKey,
    currentSlide,
    onSlideChange,
    simInvestments,
    handleSimInvest,
    handleSimValidate,
    simLoading,
    simLoadingMessage,
    showSimDecree,
    setShowSimDecree,
    simActions,
    setSimLoading,
    showBugVisualization,
    nuclearScore,
    setShowNukePopin,
    selectedTarget,
    triggerReaction,
    isTalking,
    transitionsEnabled
}) => {
    return (
        <div key={slideKey} className="h-full p-12 flex flex-col justify-center relative">
            <div className="relative z-10">

                <h2 className="text-6xl font-bold mb-4 pb-2 bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent animate-fade-in-up">
                    {slide.title}
                </h2>
                <p className="text-2xl text-blue-200/60 mb-8 animate-fade-in-up stagger-1">
                    {slide.subtitle}
                </p>

                {slide?.title === "Création de features" ?
                    <div className="grid grid-cols-1 gap-4 max-w-xl mx-auto mb-8">
                        <div
                            className={`stat-card nuclear cursor-pointer hover:scale-105 transition-all duration-500 group relative overflow-hidden border-2 flex flex-col justify-between
                            ${selectedTarget
                                    ? 'border-emerald-500 bg-emerald-950/20 shadow-[0_0_20px_rgba(16,185,129,0.3)]'
                                    : 'border-red-500/50 bg-red-950/20'}`}
                            onClick={() => {
                                if (selectedTarget) {
                                    handleSimValidate();
                                } else {
                                    setShowNukePopin(true);
                                }
                            }}
                        >
                            <div className={`absolute inset-0 transition-colors ${selectedTarget ? 'bg-emerald-500/5 group-hover:bg-emerald-500/10' : 'bg-red-500/5 group-hover:bg-red-500/10'}`} />
                            <div className="stat-label flex items-center gap-2 relative z-10">
                                <div className={`w-8 h-8 flex items-center justify-center rounded-full shadow-lg transition-all duration-500 transform ${selectedTarget ? 'bg-emerald-600 shadow-emerald-500/50 rotate-180' : 'bg-red-600 shadow-red-500/50 group-hover:scale-110'}`}>
                                    <Radiation size={16} className="text-white" />
                                </div>
                                <span className={selectedTarget ? 'text-emerald-400 font-bold' : ''}>NUCLÉAIRE</span>
                            </div>
                            <div className="stat-value relative z-10 mt-2">
                                <span className={`${selectedTarget ? 'text-emerald-400' : 'text-red-400'} font-mono`}>{nuclearScore}</span>
                                {selectedTarget ? (
                                    <span className={`${selectedTarget ? 'text-emerald-400' : 'text-red-400'} font-mono`}>Déclenchement de l'arme nucléaire</span>) : (
                                    <span className={`${selectedTarget ? 'text-emerald-400' : 'text-red-400'} font-mono`}></span>)
                                }
                                {
                                    selectedTarget ? (
                                        <div className="text-[10px] text-emerald-400/80 uppercase tracking-widest mt-1 animate-pulse font-bold">
                                            CIBLE : {selectedTarget}
                                        </div>
                                    ) : (
                                        nuclearScore >= 10 && (
                                            <div className="text-[10px] text-red-500/60 uppercase tracking-widest mt-1 group-hover:text-red-400 transition-colors">
                                                READY_TO_LAUNCH
                                            </div>
                                        )
                                    )
                                }
                            </div>
                        </div>
                    </div>
                    : <div>
                        <div className="grid grid-cols-3 gap-4 max-w-4xl mx-auto mb-8">
                            <StatCard
                                label="Économie"
                                color="economy"
                                icon={<HandCoins size={20} color="#facc15" />}
                                value={mockSimGameState.joueur.economy}
                                onInvest={handleSimInvest}
                                isSelected={simInvestments.includes("Économie")}
                                isBugVersion={showBugVisualization}
                            />
                            <StatCard
                                label="Militaire"
                                color="military"
                                icon={<Shield size={20} color="#ef4444" />}
                                value={mockSimGameState.joueur.military}
                                onInvest={handleSimInvest}
                                isSelected={simInvestments.includes("Militaire")}
                                isBugVersion={showBugVisualization}
                            />
                            <StatCard
                                label="Technologie"
                                color="technology"
                                icon={<Zap size={20} color="#3b82f6" />}
                                value={mockSimGameState.joueur.technology}
                                onInvest={handleSimInvest}
                                isSelected={simInvestments.includes("Technologie")}
                                isBugVersion={showBugVisualization}
                            />
                            <StatCard
                                label="Ressources"
                                color="resources"
                                icon={<Leaf size={20} color="#22c55e" />}
                                value={mockSimGameState.joueur.natural_resources}
                                onInvest={handleSimInvest}
                                isSelected={simInvestments.includes("Ressources")}
                                isBugVersion={showBugVisualization}
                            />
                            <StatCard
                                label="Diplomatie"
                                color="diplomacy"
                                icon={<Handshake size={20} color="#ffffff" />}
                                value={mockSimGameState.joueur.diplomacy}
                                onInvest={handleSimInvest}
                                isSelected={simInvestments.includes("Diplomatie")}
                                isBugVersion={showBugVisualization}
                            />
                            <StatCard
                                label="Stabilité"
                                color="stability"
                                icon={<Scale size={20} color="#000000" />}
                                value={mockSimGameState.joueur.stability}
                                onInvest={handleSimInvest}
                                isSelected={simInvestments.includes("Stabilité")}
                                isBugVersion={showBugVisualization}
                            />
                        </div>
                        <div className="flex justify-center">
                            <button
                                onClick={handleSimValidate}
                                disabled={simInvestments.length === 0}
                                className={`px-12 py-4 rounded-xl text-xl font-bold transition-all transform hover:scale-105 ${simInvestments.length === 0
                                    ? 'bg-slate-700 text-slate-500 cursor-not-allowed opacity-50'
                                    : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-[0_0_30px_rgba(16,185,129,0.3)] hover:shadow-[0_0_50px_rgba(16,185,129,0.5)]'
                                    }`}
                            >
                                {simInvestments.length === 0 ? "Choisissez vos investissements" : `Valider le Tour (An ${mockSimGameState.year})`}
                            </button>
                        </div>
                    </div>
                }
            </div>

            {/* Overlay de chargement simulation */}
            {simLoading && (
                <div className="fixed inset-0 z-[250] bg-slate-900/90 backdrop-blur-xl flex flex-col items-center justify-center animate-fade-in">
                    <div className="relative">
                        <div className="w-32 h-32 rounded-full border-4 border-blue-500/20 border-t-blue-400 animate-spin" />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <Brain className="w-12 h-12 text-blue-400 animate-pulse" />
                        </div>
                    </div>
                    <div className="mt-8 text-center">
                        <div className="flex items-center gap-3 justify-center mb-2">
                            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
                            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
                            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" />
                        </div>
                        <p className="text-2xl font-mono text-blue-100/90 animate-pulse">
                            {simLoadingMessage}
                        </p>
                    </div>
                </div>
            )}

            {showSimDecree && (
                <div className="fixed inset-0 z-[200] animate-fade-in">
                    <Decret
                        actions={simActions}
                        gameState={mockSimGameState}
                        setGameState={() => { }}
                        setActions={() => { }}
                        setMessage={() => { }}
                        setLoading={setSimLoading}
                        setPreviousArticles={() => { }}
                        onClose={() => setShowSimDecree(false)}
                        isSimulation={true}
                        triggerReaction={triggerReaction}
                        isTalking={isTalking}
                        transitionsEnabled={transitionsEnabled}
                        onSimulationComplete={() => {
                            setShowSimDecree(false);
                            onSlideChange(currentSlide + 1);
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default GameSimulationSlide;
